<template>
  <div class="repayment-page">
    <div class="repayment-card">
      <h1 class="repayment-title">Informacion de pago</h1>

      <div class="repayment-list">
        <div class="repayment-row">
          <div class="repayment-label">Banco de pago</div>
          <div class="repayment-value-group">
            <div class="repayment-value">{{ receiptNameValue }}</div>
            <button
              v-if="receiptNameValue !== placeholder"
              class="repayment-copy"
              type="button"
              @click="handleCopy(receiptNameValue)"
            >
              copiar
            </button>
          </div>
        </div>

        <div class="repayment-row">
          <div class="repayment-label">IBAN</div>
          <div class="repayment-value-group">
            <div class="repayment-value">{{ receiptAccountValue }}</div>
            <button
              v-if="receiptAccountValue !== placeholder"
              class="repayment-copy"
              type="button"
              @click="handleCopy(receiptAccountValue)"
            >
              copiar
            </button>
          </div>
        </div>

        <div class="repayment-row repayment-row-note">
          <div class="repayment-label">Observaciones</div>
          <div class="repayment-value-group">
            <div class="repayment-value">{{ conceptValue }}</div>
            <button
              v-if="conceptValue !== placeholder"
              class="repayment-copy"
              type="button"
              @click="handleCopy(conceptValue)"
            >
              copiar
            </button>
          </div>
          <div class="repayment-hint">
            La referencia debe rellenarse según lo indicado y debe seleccionar la transferencia inmediata. De lo contrario, el pago podría no abonarse correctamente.
          </div>
        </div>

        <div class="repayment-row">
          <div class="repayment-label">Importe</div>
          <div class="repayment-value repayment-value-amount">
            {{ repayAmountValue }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { showToast } from 'vant';

const placeholder = '--';

export default defineComponent({
  name: 'repaymentPage',
  setup() {
    const route = useRoute();

    const getQueryValue = (key: string) => {
      const value = route.query[key];

      if (Array.isArray(value)) {
        return value[0] || placeholder;
      }

      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }

      return placeholder;
    };

    const receiptNameValue = computed(() => getQueryValue('receiptName'));
    const receiptAccountValue = computed(() => getQueryValue('receiptAccount'));
    const conceptValue = computed(() => getQueryValue('concept'));
    const repayAmountValue = computed(() => getQueryValue('needRepayAmount'));

    const fallbackCopy = (value: string) => {
      const input = document.createElement('input');
      input.value = value;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    };

    const showCopySuccess = () => {
      showToast({
        message: 'Copiado',
        className: 'repayment-toast'
      });
    };

    const handleCopy = async (value: string) => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
        } else {
          fallbackCopy(value);
        }
        showCopySuccess();
      } catch (error) {
        fallbackCopy(value);
        showCopySuccess();
      }
    };

    onMounted(() => {
      document.title = 'Repayment';
    });

    return {
      placeholder,
      receiptNameValue,
      receiptAccountValue,
      conceptValue,
      repayAmountValue,
      handleCopy
    };
  }
});
</script>

<style lang="scss" scoped>
.repayment-page {
  width: 100vw;
  min-height: 100vh;
  margin-left: calc(50% - 50vw);
  padding: 24px 16px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f6f8;
}

.repayment-card {
  width: 100%;
  max-width: 520px;
  padding: 0 0 22px;
  overflow: hidden;
  border: 1px solid #e3e5ea;
  border-radius: 10px;
  box-sizing: border-box;
  background: #ffffff;
  box-shadow: 0 12px 34px rgba(32, 45, 64, 0.08);
}

.repayment-title {
  margin-bottom: 0;
  padding: 22px 20px;
  border-bottom: 1px solid #dedfe3;
  color: #111318;
  font-family: Fredoka-Medium, serif;
  font-size: 26px;
  line-height: 1.22;
  text-align: center;
}

.repayment-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 20px 28px 0;
}

.repayment-row {
  display: grid;
  grid-template-columns: 136px minmax(0, 1fr);
  gap: 14px;
  align-items: center;
  min-height: 44px;
}

.repayment-label {
  color: #1d2026;
  font-family: Fredoka-Medium, serif;
  font-size: 20px;
  line-height: 1.35;
  text-align: left;
  white-space: nowrap;
}

.repayment-value-group {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
}

.repayment-row-note {
  align-items: start;

  .repayment-label,
  .repayment-value-group {
    padding-top: 8px;
  }
}

.repayment-value {
  flex: 1;
  min-width: 0;
  color: #22262d;
  font-family: Manrope-Medium, serif;
  font-size: 20px;
  line-height: 1.4;
  text-align: left;
  word-break: break-word;
}

.repayment-value-amount {
  color: #111318;
  font-family: Manrope-Bold, serif;
}

.repayment-copy {
  flex-shrink: 0;
  min-width: 50px;
  height: 32px;
  padding: 0 10px;
  border: none;
  border-radius: 8px;
  background: #2f80ed;
  color: #ffffff;
  font-family: Manrope-Bold, serif;
  font-size: 13px;
  line-height: 1;
}

.repayment-hint {
  grid-column: 2;
  display: inline-block;
  margin-top: -6px;
  padding: 3px 8px;
  border-radius: 4px;
  background: #fff0e6;
  color: #ff5d00;
  font-family: Manrope-Medium, serif;
  font-size: 12px;
  line-height: 1.35;
}

@media (min-width: 1100px) {
  .repayment-page {
    padding: 48px 28px;
  }

  .repayment-card {
    max-width: 620px;
    padding-bottom: 26px;
    border-radius: 12px;
  }

  .repayment-title {
    padding: 24px 24px;
    font-size: 26px;
  }

  .repayment-list {
    padding: 22px 34px 0;
  }

  .repayment-row {
    grid-template-columns: 150px minmax(0, 1fr);
    min-height: 46px;
  }

  .repayment-label {
    font-size: 18px;
  }

  .repayment-value {
    font-size: 18px;
  }

  .repayment-copy {
    min-width: 50px;
    height: 30px;
    font-size: 13px;
  }

  .repayment-hint {
    font-size: 12px;
  }
}

@media (max-width: 767px) {
  .repayment-page {
    padding: 14px 8px;
  }

  .repayment-card {
    max-width: 100%;
    padding-bottom: 18px;
    border-radius: 8px;
  }

  .repayment-title {
    padding: 18px 16px;
    font-size: 20px;
  }

  .repayment-list {
    padding: 16px 18px 0;
  }

  .repayment-row {
    grid-template-columns: 106px minmax(0, 1fr);
    gap: 10px;
    min-height: 36px;
  }

  .repayment-label {
    font-size: 15px;
  }

  .repayment-value-group {
    gap: 6px;
  }

  .repayment-value {
    font-size: 15px;
  }

  .repayment-copy {
    min-width: 44px;
    height: 28px;
    padding: 0 8px;
    border-radius: 7px;
    font-size: 12px;
  }

  .repayment-hint {
    grid-column: 2;
    font-size: 11px;
  }
}
</style>
